import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import {
  DeductionRow,
  DiscountLabel,
  DueNowTotalRow,
  ThereafterRow,
} from "@/components/Layout/PriceTable/DueNowRows";
import { PriceRow } from "@/components/Layout/PriceTable/PriceRow";
import { getCurrency } from "@/utils/translationHelpers";

export type DueNowProps = {
  totalOnce: number;
  subtotalOnce: number;
  discountAmount?: number;
  /** Named basket discount (e.g. "B2B 10%"); falls back to generic "Discount". */
  discountName?: string;
  discountEndDate?: Date;
  currency: string;
  dataRateName?: string;
  dataRatePrice?: number;
  billingPeriod: string;
  recurringCaption?: string;
  recurringAmount?: number;
  recurringDate?: string;
  recurringBillingPeriod?: string;
  voiceOptionName?: string | ReactNode;
  voiceOptionPrice?: number;
  watchSimName?: string;
  watchSimPrice?: number;
  credit?: number;
  creditTimeRemaining?: number;
  icon?: ReactNode;
  isPrecharged?: boolean;
  hideSubtotal?: boolean;
};

// TODO - Refactor the RoamingDueNow DeviceTile so they make more sense
export const DueNow = ({
  totalOnce,
  subtotalOnce,
  discountAmount,
  discountName,
  discountEndDate,
  currency,
  isPrecharged,
  billingPeriod,
  recurringCaption,
  recurringAmount,
  recurringDate,
  recurringBillingPeriod,
  dataRateName,
  dataRatePrice,
  voiceOptionName,
  voiceOptionPrice,
  watchSimName,
  watchSimPrice,
  credit,
  creditTimeRemaining,
  icon,
  hideSubtotal,
}: DueNowProps) => {
  const { t } = useTranslation();

  const hasCredit = !!credit && credit > 0;
  const hasCreditTimeRemaining =
    !!creditTimeRemaining && creditTimeRemaining > 0;
  const hasDiscount = !!discountAmount && discountAmount > 0;

  const shouldShowSubtotalSection = hideSubtotal
    ? hasCredit || hasCreditTimeRemaining || hasDiscount
    : !!subtotalOnce &&
      subtotalOnce > 0 &&
      (hasCredit ||
        hasCreditTimeRemaining ||
        hasDiscount ||
        subtotalOnce > totalOnce) &&
      (!isPrecharged || hasCredit || hasCreditTimeRemaining || hasDiscount);

  const showVoiceOption =
    !dataRateName?.startsWith("Flat Mobile") &&
    !!voiceOptionName &&
    voiceOptionPrice !== undefined &&
    voiceOptionPrice >= 0;

  const resolvedDiscountName =
    discountName ?? t("Onboarding.sections.checkout.summary.discount");

  return (
    <CardModule
      className="self-start"
      variant="white"
      title={
        <Trans components={[<span key="dueNow" />]} t={t}>
          Onboarding.sections.checkout.summary.payOnce
        </Trans>
      }
      actions={icon ? <div id="device-icon">{icon}</div> : undefined}
    >
      <div className="flex w-full flex-col">
        {!!dataRateName &&
          dataRatePrice !== undefined &&
          dataRatePrice >= 0 && (
            <PriceRow
              withBackdrop
              variant="strong"
              label={dataRateName}
              value={
                <span data-testid="flat-price">
                  {getCurrency(dataRatePrice, currency)}
                </span>
              }
            />
          )}

        {showVoiceOption && (
          <PriceRow
            label={
              <>
                {voiceOptionName}
                <br />
                <span className="text-body-xs font-regular leading-md">
                  {billingPeriod}
                </span>
              </>
            }
            value={
              <span data-testid="voice-price">
                {getCurrency(voiceOptionPrice, currency)}
              </span>
            }
          />
        )}

        {!!watchSimName &&
          watchSimPrice !== undefined &&
          watchSimPrice >= 0 && (
            <PriceRow
              label={
                <>
                  {watchSimName}
                  <br />
                  <span className="text-body-xs font-regular leading-md">
                    {billingPeriod}
                  </span>
                </>
              }
              value={
                <span data-testid="watch-price">
                  {getCurrency(watchSimPrice, currency)}
                </span>
              }
            />
          )}

        {shouldShowSubtotalSection && (
          <>
            {!hideSubtotal && (
              <PriceRow
                label={t("Onboarding.sections.checkout.summary.subtotal")}
                value={getCurrency(subtotalOnce, currency)}
              />
            )}

            {hasDiscount && (
              <DeductionRow
                label={
                  <DiscountLabel
                    name={resolvedDiscountName}
                    endDate={discountEndDate}
                  />
                }
                amount={discountAmount}
              />
            )}

            {hasCreditTimeRemaining && (
              <DeductionRow
                withDivider
                label={
                  <>
                    {t("Onboarding.sections.checkout.summary.credit")}{" "}
                    <span className="text-body-xs font-regular leading-md">
                      {t("cockpit.managementTile.tariff.checkout.creditText")}
                    </span>
                  </>
                }
                amount={creditTimeRemaining}
              />
            )}

            {hasCredit && (
              <DeductionRow
                withDivider
                label={
                  <>
                    {t("Onboarding.sections.checkout.summary.credit")}{" "}
                    <span className="text-body-xs font-regular leading-md">
                      {t(
                        "cockpit.managementTile.tariff.checkout.creditTextOthers",
                      )}
                    </span>
                  </>
                }
                amount={credit}
              />
            )}
          </>
        )}

        <DueNowTotalRow
          dataTestid="total-amount-due-now"
          label={t("Onboarding.sections.checkout.summary.total")}
          amount={totalOnce}
          currency={currency}
          caption={recurringCaption}
        />

        {recurringAmount !== undefined &&
          recurringAmount !== Math.max(totalOnce, 0) && (
            <ThereafterRow
              amount={recurringAmount}
              currency={currency}
              date={recurringDate}
              billingPeriod={recurringBillingPeriod ?? billingPeriod}
            />
          )}
      </div>
    </CardModule>
  );
};
